/**
 * We need to create a mirror div with the same styles as the textarea to accurately calculate
 * the pixel coordinates of the caret. This is because textareas don't expose this info directly.
 */

// The properties we need to copy from the textarea to the mirror div
const properties = [
    'direction',
    'boxSizing',
    'width',
    'height',
    'overflowX',
    'overflowY',
    'borderTopWidth',
    'borderRightWidth',
    'borderBottomWidth',
    'borderLeftWidth',
    'paddingTop',
    'paddingRight',
    'paddingBottom',
    'paddingLeft',
    'fontStyle',
    'fontVariant',
    'fontWeight',
    'fontStretch',
    'fontSize',
    'fontSizeAdjust',
    'lineHeight',
    'fontFamily',
    'textAlign',
    'textTransform',
    'textIndent',
    'textDecoration',
    'letterSpacing',
    'wordSpacing',
] as const;

export type CaretCoordinates = {
    top: number;
    left: number;
    height: number;
};

export function getCaretCoordinates(element: HTMLTextAreaElement, position: number): CaretCoordinates {
    // We'll use a single off-screen div for all calculations to avoid thrashing the DOM
    let split = getMirrorDiv();
    const div = split.div;
    const computed = window.getComputedStyle(element);

    // Copy all relevant styles
    properties.forEach(prop => {
        div.style[prop] = computed[prop];
    });

    // Explicitly set some styles to ensure the mirror behaves exactly like the textarea
    div.style.whiteSpace = 'pre-wrap';
    div.style.wordWrap = 'break-word';
    div.style.position = 'absolute';
    div.style.visibility = 'hidden';

    // For Firefox behavior with scrollbars
    const isFirefox = (window as any).mozInnerScreenX != null;
    if (isFirefox) {
        if (element.scrollHeight > parseInt(computed.height))
            div.style.overflowY = 'scroll';
    } else {
        div.style.overflow = 'hidden';
    }

    div.textContent = element.value.substring(0, position);

    // The span is the "cursor"
    const span = document.createElement('span');
    span.textContent = element.value.substring(position) || '.';
    div.appendChild(span);

    const coordinates = {
        top: span.offsetTop + parseInt(computed['borderTopWidth']),
        left: span.offsetLeft + parseInt(computed['borderLeftWidth']),
        height: parseInt(computed['lineHeight'])
    };

    // Cleanup
    document.body.removeChild(div);

    return coordinates;
}

function getMirrorDiv() {
    // Create a new div every time to ensure cleanliness, or we could cache it.
    // Given this isn't high frequency (typing speed), creating new is safer/simpler.
    const div = document.createElement('div');
    document.body.appendChild(div);
    return { div };
}
